<template>
    <BaseModal
        transition-direction="right"
        blur-background
        disable-mobile-blur
        :is-open="isOpen"
        class="overflow-y-hidden"
        @close="closeModal">
        <div
            :class="
                isCromi()
                    ? 'max-w-[19.625rem] sm:max-w-[20.625rem]'
                    : 'max-w-[19.625rem]'
            "
            class="absolute right-0 top-3 w-full md:top-10">
            <div
                class="relative z-50 flex h-[calc(100dvh_-_12px)] flex-col items-end rounded-l-20 bg-white p-3 pt-6 transition-transform duration-[400ms] before:absolute before:left-[99%] before:top-0 before:size-full before:translate-x-0 before:bg-white">
                <div class="flex w-full shrink-0 flex-wrap gap-y-3 md:mt-3">
                    <p
                        v-if="isRent()"
                        class="w-full basis-full text-24/133 font-semibold text-accent-400">
                        Аренда:
                    </p>
                    <div
                        v-if="config.public.website !== 'radioguide.rent'"
                        role="search"
                        class="flex w-full max-w-[85%] basis-4/5 items-center rounded-full border border-accent text-accent">
                        <input
                            tabindex="-1"
                            placeholder="Введите Ваш за..."
                            readonly
                            class="w-full rounded-full bg-transparent py-0 pl-3 tracking-wider placeholder-accent-100 outline-none placeholder:text-16/120 placeholder:font-semibold placeholder:tracking-wider md:py-3"
                            @click="openSearchModal" />
                        <button
                            aria-label="поиск"
                            class="pr-2 outline-none">
                            <AtomIcon
                                name="search"
                                class="size-10" />
                        </button>
                    </div>
                    <button
                        aria-label="Закрыть бургер-меню"
                        class="flex basis-1/5 items-center justify-center outline-none focus:outline-none"
                        @click="closeModal">
                        <AtomIcon
                            name="close"
                            class="size-7 text-accent" />
                    </button>
                </div>

                <div
                    :class="
                        isCromi()
                            ? 'min-h-0 flex-1 overflow-y-auto overscroll-contain pb-4 pr-1'
                            : 'w-full'
                    "
                    class="w-full">
                    <NuxtLink
                        v-if="isRadioguide()"
                        to="/"
                        class="pointer-events-auto mt-5 flex w-full flex-col space-y-3"
                        @click="closeModal">
                        <div
                            class="relative h-full max-h-[160px] overflow-hidden rounded-8 bg-accent-400">
                            <picture>
                                <source
                                    srcset="
                                        /assets/images/products/radioguide/webp/reinvox-duo-bright.webp
                                    "
                                    type="image/webp" />
                                <img
                                    width="549"
                                    height="934"
                                    decoding="async"
                                    loading="lazy"
                                    src="/assets/images/products/radioguide/jpg-png/reinvox-duo-bright.png"
                                    class="relative top-6 mx-auto max-w-[113px]" />
                            </picture>
                        </div>

                        <p
                            class="relative z-10 inline-flex items-center text-16/120 font-semibold text-ash">
                            Радиогиды в аренду
                            <AtomIcon
                                name="arrow-right"
                                class="ml-2" />
                        </p>
                    </NuxtLink>

                    <BurgerMenuCategories
                        v-if="config.public.website !== 'radioguide.rent'"
                        class="mt-5"
                        @click="closeModal" />

                    <div
                        :class="isCromi() ? 'grid grid-cols-2 gap-2' : 'gap-2'"
                        class="mt-3 flex w-full justify-end text-16/132 font-semibold leading-5 text-accent">
                        <div
                            v-for="(group, index) in linksGroupsByDirection"
                            :key="index"
                            :class="[
                                index ? 'w-full' : '',
                                isCromi() && index !== 2
                                    ? 'flex-col'
                                    : isCromi() && index === 2
                                      ? 'col-span-2 w-full'
                                      : 'flex-col',
                            ]"
                            class="flex gap-2">
                            <NuxtLink
                                v-for="link in group"
                                :key="link.title"
                                :to="link.to"
                                :class="[
                                    isCromi()
                                        ? 'h-[2.6rem] max-sm:h-[2.6rem]'
                                        : 'h-[3.125rem] max-sm:h-[3rem]',
                                    isCromi() && index === 2 ? 'w-full' : '',
                                ]"
                                class="content-center rounded-8 bg-ash-200 p-2 text-14/132"
                                @click="closeModal">
                                <div v-html="link.title"></div>
                            </NuxtLink>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </BaseModal>
</template>

<script setup lang="ts">
import AtomIcon from '~/components/atom/atom-icon/AtomIcon.vue'
import BaseModal from '~/components/base/base-modal/BaseModal.vue'
import BurgerMenuCategories from '~/components/modals/burger-menu/BurgerMenuCategories.vue'

defineProps({
    isOpen: {
        type: Boolean as PropType<boolean>,
        default: false,
    },
})

const emits = defineEmits(['openSearchModal'])

function openSearchModal() {
    closeModal()
    emits('openSearchModal')
}

const linkGroups = [
    [
        {
            title: 'Аренда',
            to: '/rent',
        },
        {
            title: 'Решения',
            to: '/solutions',
        },
        {
            title: 'О компании',
            to: '/about',
        },
        {
            title: 'Контакты',
            to: '/contacts',
        },
        {
            title: 'Доставка и&nbsp;оплата',
            to: '/delivery-and-payment',
        },
    ],
    [
        {
            title: 'Портфолио',
            to: '/portfolio',
        },
        {
            title: 'СМИ о нас',
            to: '/media',
        },
        {
            title: 'Гарантия и&nbsp;сервис',
            to: '/warranty-and-service',
        },
        {
            title: 'Вопрос-ответ',
            to: '/faq',
        },
        {
            title: 'Видео',
            to: '/videos',
        },
    ],
    [
        {
            title: 'Отзывы',
            to: '/reviews',
        },
        {
            title: 'Блог PRO',
            to: '/pro',
        },
        {
            title: 'Новости',
            to: '/news',
        },
    ],
]

const linkGroupsRadioguide = [
    [
        {
            title: 'Доставка по Москве и СПб',
            to: '/delivery-msk-and-spb',
        },
        {
            title: 'Доставка в другие города',
            to: '/delivery-russia',
        },
        {
            title: 'Условия оплаты',
            to: '/pay',
        },
        {
            title: 'Купить такое оборудование',
            to: 'https://cromi.ru/radioguide',
        },
        {
            title: 'О компании',
            to: '/about',
        },
    ],
    [
        {
            title: 'Отзывы',
            to: '/reviews',
        },
        {
            title: 'Вопрос-ответ',
            to: '/faq',
        },
        {
            title: 'Опыт',
            to: '/portfolio',
        },
        {
            title: 'Блог PRO',
            to: '/pro',
        },
        {
            title: 'Видео',
            to: '/videos',
        },
        {
            title: 'Контакты',
            to: '/contacts',
        },
    ],
]

const linkGroupsRent = [
    [
        {
            title: 'Продажа оборудования',
            to: '/sale',
        },
        {
            title: 'О компании',
            to: '/about',
        },
        {
            title: 'Контакты',
            to: '/contacts',
        },
        {
            title: 'Отзывы',
            to: '/reviews',
        },
    ],
    [
        {
            title: 'Портфолио',
            to: '/portfolio',
        },
        {
            title: 'Вопрос-ответ',
            to: '/faq',
        },
        {
            title: 'Блог PRO',
            to: '/pro',
        },
        {
            title: 'Видео',
            to: '/videos',
        },
    ],
]

const config = useRuntimeConfig()
const modalStore = useModalStore()

const linksGroupsByDirection = computed(() => {
    if (isRent()) return linkGroupsRent
    if (isRadioguide()) return linkGroupsRadioguide
    return linkGroups
})

function closeModal() {
    modalStore.close('mobileMenu')
}
</script>
